import React, { useEffect, useRef, useState } from "react";
import { MoreVertical, Upload, Link as LinkIcon, ImageIcon } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import api, { fileSrc, API } from "@/lib/api";
import { PRESET_SILHOUETTES } from "@/lib/defaultTemplate";

const snap = (n) => Math.round(n / 10) * 10;

export default function Silhouette({ silhouette, onChange, readOnly, shareToken }) {
  const [drag, setDrag] = useState(null);
  const [resize, setResize] = useState(null);
  const fileInput = useRef(null);

  let src;
  if (silhouette.type === "preset") {
    src = PRESET_SILHOUETTES.find((p) => p.key === silhouette.value)?.url;
  } else if (silhouette.type === "upload") {
    src = shareToken ? `${API}/shared/${shareToken}/file` : fileSrc(silhouette.value);
  } else {
    src = silhouette.value;
  }

  useEffect(() => {
    if (readOnly) return undefined;
    if (!drag && !resize) return undefined;
    const move = (e) => {
      if (drag) {
        const dx = e.clientX - drag.startX, dy = e.clientY - drag.startY;
        onChange({ ...silhouette, x: Math.max(0, snap(drag.x + dx)), y: Math.max(0, snap(drag.y + dy)) });
      } else if (resize) {
        const dx = e.clientX - resize.startX, dy = e.clientY - resize.startY;
        onChange({ ...silhouette, w: Math.max(160, snap(resize.w + dx)), h: Math.max(200, snap(resize.h + dy)) });
      }
    };
    const up = () => { setDrag(null); setResize(null); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [drag, resize, silhouette, onChange, readOnly]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onChange({ ...silhouette, type: "upload", value: data.url });
  };

  const onPickUrl = () => {
    const u = window.prompt("Paste an image URL:");
    if (u) onChange({ ...silhouette, type: "url", value: u });
  };

  return (
    <div
      className="absolute group rounded-lg border border-dashed border-[#212A3E] bg-gradient-to-b from-[#0d1320]/60 to-[#0a0f1c]/60 transition-colors hover:border-[#D4AF37]/40"
      style={{ left: silhouette.x, top: silhouette.y, width: silhouette.w, height: silhouette.h }}
      data-testid="silhouette-box"
    >
      <div
        className={`flex h-full w-full select-none items-center justify-center overflow-hidden ${readOnly ? "" : "cursor-move"}`}
        onMouseDown={readOnly ? undefined : (e) => {
          if (e.target.closest("[data-no-drag]")) return;
          setDrag({ startX: e.clientX, startY: e.clientY, x: silhouette.x, y: silhouette.y });
        }}
      >
        {src ? (
          <img src={src} alt="silhouette" className="max-h-full max-w-full object-contain opacity-90" draggable={false} />
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#A0ABC0]">
            <ImageIcon className="h-8 w-8" />
            <span className="text-xs">No image</span>
          </div>
        )}
      </div>

      {/* Menu — hidden in read-only mode */}
      {!readOnly && (
      <div className="absolute right-2 top-2" data-no-drag>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded bg-[#0a0f1c]/80 p-1 text-[#A0ABC0] hover:text-white" data-testid="silhouette-menu">
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-[#212A3E] bg-[#101623] text-white">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-[#A0ABC0]">Presets</DropdownMenuLabel>
            {PRESET_SILHOUETTES.map((p) => (
              <DropdownMenuItem
                key={p.key}
                onClick={() => onChange({ ...silhouette, type: "preset", value: p.key })}
                data-testid={`silhouette-preset-${p.key}`}
              >
                {p.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-[#212A3E]" />
            <DropdownMenuItem onClick={() => fileInput.current?.click()} data-testid="silhouette-upload">
              <Upload className="mr-2 h-4 w-4" />Upload image
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPickUrl} data-testid="silhouette-url">
              <LinkIcon className="mr-2 h-4 w-4" />Image URL
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <input
          ref={fileInput} type="file" accept="image/*" className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }}
        />
      </div>
      )}

      {/* Resize handle — hidden in read-only mode */}
      {!readOnly && (
      <div
        onMouseDown={(e) => { e.stopPropagation(); setResize({ startX: e.clientX, startY: e.clientY, w: silhouette.w, h: silhouette.h }); }}
        className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
        data-no-drag
        style={{
          background:
            "linear-gradient(135deg, transparent 50%, rgba(212,175,55,0.6) 50%, rgba(212,175,55,0.6) 60%, transparent 60%, transparent 70%, rgba(212,175,55,0.6) 70%, rgba(212,175,55,0.6) 80%, transparent 80%)",
        }}
      />
      )}
    </div>
  );
}
